// Rental underwriting for one typical home: buy at the area's typical value, rent at its typical rent. Pure (no
// imports) so the web panel and the server share it; src/computed.ts mirrors it in SQL for the map, tested equal.

export interface UnderwriteInput {
  /** purchase price, $ */
  price: number;
  /** monthly rent, $ */
  rent: number;
  /** property tax + home insurance, $ per year */
  taxIns: number;
  /** yearly rate in percent (6.5 = 6.5%) */
  rate: number;
  termYears: number;
  /** shares (0.25 = 25%) */
  down: number;
  closing: number;
  /** shares of the rent */
  vacancy: number;
  management: number;
  maintenance: number;
}

/** Standard investor assumptions (the map's metrics use these; the panel starts from them). */
export const UNDERWRITE_DEFAULTS = {
  down: 0.25,
  closing: 0.03,
  vacancy: 0.05,
  management: 0.08,
  maintenance: 0.1,
  termYears: 30,
  /** added to the month's average 30-year rate: investment-property loans cost more than owner-occupied ones */
  ratePremium: 0.5,
};

/** Monthly payment per dollar borrowed. */
export function paymentFactor(ratePct: number, months: number): number {
  const i = ratePct / 1200;
  return i > 0 ? i / (1 - (1 + i) ** -months) : 1 / months;
}

export function underwrite(x: UnderwriteInput) {
  const keep = 1 - x.vacancy - x.management - x.maintenance; // share of the rent left after vacancy and upkeep
  const noi = x.rent * 12 * keep - x.taxIns; // net operating income, $ / year
  const loan = x.price * (1 - x.down);
  const debt = loan * paymentFactor(x.rate, Math.round(x.termYears * 12)) * 12; // $ / year
  const cashIn = x.price * (x.down + x.closing);
  const flow = noi - debt;
  return {
    noi,
    debt,
    cashIn,
    /** $ / month after every cost and the mortgage */
    cashFlow: flow / 12,
    /** yearly cash flow / cash put in */
    cashOnCash: cashIn > 0 ? flow / cashIn : null,
    /** NOI / debt service (lenders want about 1.2+) */
    dscr: debt > 0 ? noi / debt : null,
    /** NOI / price */
    capRate: x.price > 0 ? noi / x.price : null,
    /** the rent at which the cash flow is zero, $ / month */
    breakEvenRent: keep > 0 ? (x.taxIns + debt) / 12 / keep : null,
  };
}
